/**
 * Header looks — how the site's top menu is laid out on a computer, and how the menu opens on a
 * phone. Two picks stored in the site's existing `section_looks` (keys `header` and `mobileMenu`),
 * written by the same PUT /site-config the other Pick-a-Look choices use — no store of their own.
 *
 * ONE definition, read by both renderers: the live header (apps/tenant-sites Navigation.tsx) and
 * the Studio preview's nav block (packages/blocks Nav.tsx), plus the picker's option list. A
 * missing or unknown value is the original layout, so every existing site renders exactly as it
 * did before these existed.
 *
 * Every style keeps the bar one row tall. The row's height is headerBarSizes() below (64px unless
 * the owner made it taller or the logo needs more); the tenant layout publishes it as
 * --header-height, and a spacer under the fixed header (--header-extra) pushes every page down by
 * whatever the bar is taller than the 4rem the pages were padded for.
 */

export type HeaderStyle = 'classic' | 'centered' | 'split' | 'button' | 'minimal';
export type MobileMenuStyle = 'dropdown' | 'drawer' | 'fullscreen';

export const HEADER_STYLES: ReadonlyArray<{ key: HeaderStyle; name: string; blurb: string }> = [
  { key: 'classic', name: 'Classic', blurb: 'Your logo on the left, the menu on the right.' },
  { key: 'centered', name: 'Centered', blurb: 'Your logo on the left, the menu in the middle of the bar.' },
  { key: 'split', name: 'Split', blurb: 'Your logo in the middle, the menu split either side of it.' },
  { key: 'button', name: 'Button', blurb: 'Classic, with the last menu link shown as a button in your brand color.' },
  { key: 'minimal', name: 'Minimal', blurb: 'Just your logo and a Menu button, on every screen size.' },
];

export const MOBILE_MENU_STYLES: ReadonlyArray<{ key: MobileMenuStyle; name: string; blurb: string }> = [
  { key: 'dropdown', name: 'Drop-down', blurb: 'The menu drops down under the top bar.' },
  { key: 'drawer', name: 'Side drawer', blurb: 'The menu slides in from the right side.' },
  { key: 'fullscreen', name: 'Full screen', blurb: 'The menu fills the screen with large links.' },
];

const HEADER_KEYS = new Set<string>(HEADER_STYLES.map((s) => s.key));
const MOBILE_KEYS = new Set<string>(MOBILE_MENU_STYLES.map((s) => s.key));

/** The header style to render. Anything unknown (or nothing) is Classic — today's layout. */
export function resolveHeaderStyle(value: unknown): HeaderStyle {
  return typeof value === 'string' && HEADER_KEYS.has(value) ? (value as HeaderStyle) : 'classic';
}

/** The phone menu style to render. Anything unknown (or nothing) is the drop-down — today's menu. */
export function resolveMobileMenuStyle(value: unknown): MobileMenuStyle {
  return typeof value === 'string' && MOBILE_KEYS.has(value) ? (value as MobileMenuStyle) : 'dropdown';
}

/**
 * The Split header's two halves: the first half of the menu goes left of the logo, the rest right.
 * An odd count puts the extra link on the left (the right side also carries Login).
 */
export function splitMenu<T>(items: readonly T[]): { left: T[]; right: T[] } {
  const cut = Math.ceil(items.length / 2);
  return { left: items.slice(0, cut), right: items.slice(cut) };
}

/**
 * How the header sits over the home page's hero at the top of the page. Stored as `overHero` on
 * the home page's Navigation section (the header settings' one home, next to its colours), and
 * read by the live header, the Simon Gray header and the Studio preview.
 *
 *  - auto: the header decides, as it always has — transparent over a full-width hero at the top
 *    of the home page, solid over a contained hero look and on every other page.
 *  - transparent: transparent at the top of the home page whatever the hero, solid once scrolled.
 *  - solid: the solid bar everywhere, the home page's top included.
 *
 * Missing or unknown is auto, so every site renders exactly as it did before this existed. (The
 * nav block's older `overlayHero` switch was never read by the live header; it stays a preview-
 * only prop the Studio computes.) A header background colour always makes the bar solid.
 */
export type HeaderOverHero = 'auto' | 'transparent' | 'solid';

export const HEADER_OVER_HERO: ReadonlyArray<{ key: HeaderOverHero; name: string; blurb: string }> = [
  { key: 'auto', name: 'Automatic', blurb: 'See-through over a full-width hero photo at the top of Home, solid everywhere else.' },
  { key: 'transparent', name: 'See-through', blurb: 'See-through at the top of Home over any hero, solid once a visitor scrolls.' },
  { key: 'solid', name: 'Solid', blurb: 'The solid bar on every page, the top of Home included.' },
];

/** The over-the-hero setting to render. Anything unknown (or nothing) is auto — today's behaviour. */
export function resolveHeaderOverHero(value: unknown): HeaderOverHero {
  return value === 'transparent' || value === 'solid' ? value : 'auto';
}

/**
 * How wide the header's row is: the logo and menu sit inside a centred box this wide, and the bar's
 * colour still runs edge to edge. Stored as `headerWidth` in the site's `section_looks` beside the
 * layout and phone menu (the same PUT /site-config save, so no store of its own), and read by the
 * live header, the Simon Gray header and the Studio preview.
 *
 * Missing or unknown is Standard, the 80rem (Tailwind max-w-7xl) box every header has always used,
 * so no existing header moves.
 */
export type HeaderWidth = 'narrow' | 'standard' | 'wide' | 'full';

export const HEADER_WIDTHS: ReadonlyArray<{ key: HeaderWidth; name: string; blurb: string; maxWidth: string }> = [
  { key: 'narrow', name: 'Narrow', blurb: 'The logo and menu closer together, in a 1024px box.', maxWidth: '64rem' },
  { key: 'standard', name: 'Standard', blurb: 'The usual 1280px box.', maxWidth: '80rem' },
  { key: 'wide', name: 'Wide', blurb: 'A wider 1536px box, for big screens.', maxWidth: '96rem' },
  { key: 'full', name: 'Full width', blurb: 'The logo at the far left and the menu at the far right of the screen.', maxWidth: 'none' },
];

/** The header width to render. Anything unknown (or nothing) is Standard — today's width. */
export function resolveHeaderWidth(value: unknown): HeaderWidth {
  return value === 'narrow' || value === 'wide' || value === 'full' ? value : 'standard';
}

/**
 * The inline max-width for the header's row, or undefined for Standard: the row keeps its
 * max-w-7xl class then, so a header nobody widened renders exactly as before.
 */
export function headerRowMaxWidth(value: unknown): string | undefined {
  const width = resolveHeaderWidth(value);
  return width === 'standard' ? undefined : HEADER_WIDTHS.find((w) => w.key === width)!.maxWidth;
}

/**
 * Whether the header shows its Login button (the LoginChooser: agents to the tenant admin, home
 * shoppers to their saved homes). Stored as `headerLogin` in the site's `section_looks` beside the
 * layout, width and height (the same PUT /site-config save, so no store of its own), and read by
 * the live header, the Simon Gray header and the Studio preview, on desktop and in the phone menu.
 *
 * Only the exact value 'off' hides it: missing or anything else is On, so every existing site
 * keeps its Login button.
 */
export function headerShowsLogin(value: unknown): boolean {
  return value !== 'off';
}

/**
 * The header logo's height, in pixels. Stored as the site's style `logo_height` (Brand Manager's
 * slider, Structure → Header and BLT's logo tool all save it there). The bar is one 64px row (see
 * above), so the biggest logo that fits is 56px: 4px clear above and below. A stored value above
 * that draws at 56px, never past the bar; none at all is 40px.
 */
export const HEADER_LOGO_MIN_PX = 16;
export const HEADER_LOGO_DEFAULT_PX = 40;

/**
 * The header bar's height (Dawn, 2026-10-26: "make the header higher … and have the header resize
 * higher when the logo is made bigger"). `section_looks.headerHeight` holds the owner's chosen bar
 * height in px (unset = the 64px bar every header has always had); the bar also grows to fit the
 * logo, so it is never shorter than the logo plus HEADER_LOGO_GAP_PX.
 */
export const HEADER_HEIGHT_MIN_PX = 64;
export const HEADER_HEIGHT_MAX_PX = 160;
export const HEADER_HEIGHT_DEFAULT_PX = 64;
/** Room above and below the logo inside the bar (12px each side). */
export const HEADER_LOGO_GAP_PX = 24;
/** The biggest logo the tallest bar fits. */
export const HEADER_LOGO_MAX_PX = HEADER_HEIGHT_MAX_PX - HEADER_LOGO_GAP_PX;
/** On a phone the bar never grows past this, so a big logo can't eat the screen. */
export const HEADER_PHONE_HEIGHT_MAX_PX = 88;

const toNum = (value: unknown): number =>
  typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;

export function headerLogoPx(value: unknown): number {
  const n = toNum(value);
  if (!Number.isFinite(n) || n <= 0) return HEADER_LOGO_DEFAULT_PX;
  return Math.max(HEADER_LOGO_MIN_PX, Math.min(HEADER_LOGO_MAX_PX, Math.round(n)));
}

/** The owner's chosen bar height (section_looks.headerHeight), clamped; unset = 64px. */
export function headerHeightPx(value: unknown): number {
  const n = toNum(value);
  if (!Number.isFinite(n) || n <= 0) return HEADER_HEIGHT_DEFAULT_PX;
  return Math.max(HEADER_HEIGHT_MIN_PX, Math.min(HEADER_HEIGHT_MAX_PX, Math.round(n)));
}

export interface HeaderBarSize { bar: number; logo: number }

/**
 * The bar and logo the header draws, on a computer and on a phone: the chosen height, grown to fit
 * the logo. One function for the live header (layout CSS variables) and the Studio preview.
 */
export function headerBarSizes(heightSetting: unknown, logoSetting: unknown): { desktop: HeaderBarSize; phone: HeaderBarSize } {
  const logo = headerLogoPx(logoSetting);
  const bar = Math.max(headerHeightPx(heightSetting), logo + HEADER_LOGO_GAP_PX);
  const phoneBar = Math.min(bar, HEADER_PHONE_HEIGHT_MAX_PX);
  return {
    desktop: { bar, logo },
    phone: { bar: phoneBar, logo: Math.min(logo, phoneBar - HEADER_LOGO_GAP_PX) },
  };
}
